import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT_INFO, SERVICE_KEYS } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const updateField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", message: "" });
    try {
      const payload = { ...form, language: lang };
      if (!payload.service) delete payload.service;
      if (!payload.phone) delete payload.phone;
      await axios.post(`${API}/contact`, payload);
      setStatus({ type: "success", message: t.contact.successDetail });
      setForm({ first_name: "", last_name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      setStatus({ type: "error", message: t.contact.error });
    }
  };

  const wa = `https://wa.me/${CONTACT_INFO.whatsappClean}?text=${encodeURIComponent(t.whatsapp.message)}`;

  return (
    <div data-testid="page-contact">
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
              <span className="h-px w-8 bg-[#0022FF]" />
              {t.nav.contact}
            </div>
            <h1 className="font-display font-black text-slate-900 text-5xl sm:text-6xl lg:text-7xl tracking-tight">
              {t.contact.title}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.contact.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Info */}
            <div className="lg:col-span-5 space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl bg-[#0022FF] text-white p-8 relative overflow-hidden"
              >
                <div className="absolute inset-0 grain-texture opacity-30" />
                <div className="relative">
                  <h3 className="font-display font-black text-2xl tracking-tight mb-6">
                    {t.contact.infoTitle}
                  </h3>
                  <ul className="space-y-5 text-sm">
                    <li className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <MapPin className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-sky-200 uppercase text-[10px] tracking-wider">
                          {t.contact.address}
                        </div>
                        <div className="mt-1 text-base">{CONTACT_INFO.street}</div>
                        <div className="text-base">{CONTACT_INFO.city}</div>
                      </div>
                    </li>
                    <li>
                      <a
                        href={`tel:${CONTACT_INFO.phoneClean}`}
                        data-testid="contact-call-link"
                        className="flex items-start gap-4 group"
                      >
                        <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0 group-hover:bg-white/20 transition-colors">
                          <Phone className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-semibold text-sky-200 uppercase text-[10px] tracking-wider">
                            Telefon
                          </div>
                          <div className="mt-1 text-base font-medium">{CONTACT_INFO.phone}</div>
                        </div>
                      </a>
                    </li>
                    <li>
                      <a
                        href={`mailto:${CONTACT_INFO.email}`}
                        data-testid="contact-email-link"
                        className="flex items-start gap-4 group"
                      >
                        <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0 group-hover:bg-white/20 transition-colors">
                          <Mail className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-semibold text-sky-200 uppercase text-[10px] tracking-wider">
                            E-Mail
                          </div>
                          <div className="mt-1 text-base font-medium">{CONTACT_INFO.email}</div>
                        </div>
                      </a>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <Clock className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-sky-200 uppercase text-[10px] tracking-wider">
                          {t.contact.hours}
                        </div>
                        <div className="mt-1 text-base whitespace-pre-line">{t.contact.hoursText}</div>
                      </div>
                    </li>
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href={wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90"
                    >
                      <MessageCircle className="h-4 w-4" />
                      {t.contact.whatsappCta}
                    </a>
                    <a
                      href={`tel:${CONTACT_INFO.phoneClean}`}
                      className="inline-flex items-center gap-2 rounded-full bg-white text-[#0022FF] px-5 py-2.5 text-sm font-semibold hover:bg-sky-50"
                    >
                      <Phone className="h-4 w-4" />
                      {t.contact.callCta}
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Map */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="rounded-3xl overflow-hidden border border-slate-200 h-72"
              >
                <iframe
                  src={CONTACT_INFO.mapsEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps - SHK Haustechnik"
                  data-testid="contact-map"
                />
              </motion.div>
            </div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="lg:col-span-7"
            >
              <div className="rounded-3xl bg-white border border-slate-200 p-8 lg:p-10 shadow-sm">
                <h3 className="font-display font-black text-3xl text-slate-900 tracking-tight mb-6">
                  {t.contact.formTitle}
                </h3>

                {status.type === "success" ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid="contact-form-success"
                    className="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 text-center"
                  >
                    <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
                    <h4 className="font-display font-bold text-2xl text-emerald-900 mb-2">
                      {t.contact.success}
                    </h4>
                    <p className="text-emerald-700">{status.message}</p>
                    <button
                      onClick={() => setStatus({ type: "idle", message: "" })}
                      className="mt-6 text-sm font-semibold text-emerald-700 hover:underline"
                    >
                      ↻
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field
                        label={t.contact.firstName}
                        required
                        testId="contact-first-name"
                        value={form.first_name}
                        onChange={updateField("first_name")}
                      />
                      <Field
                        label={t.contact.lastName}
                        required
                        testId="contact-last-name"
                        value={form.last_name}
                        onChange={updateField("last_name")}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field
                        label={t.contact.email}
                        type="email"
                        required
                        testId="contact-email"
                        value={form.email}
                        onChange={updateField("email")}
                      />
                      <Field
                        label={t.contact.phone}
                        type="tel"
                        testId="contact-phone"
                        value={form.phone}
                        onChange={updateField("phone")}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                        {t.contact.service}
                      </label>
                      <select
                        value={form.service}
                        onChange={updateField("service")}
                        data-testid="contact-service"
                        className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#0022FF] focus:ring-2 focus:ring-[#0022FF]/20 transition"
                      >
                        <option value="">{t.contact.selectService}</option>
                        {SERVICE_KEYS.map((k) => (
                          <option key={k} value={k}>
                            {t.services[k].name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                        {t.contact.message} <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        rows={5}
                        required
                        minLength={5}
                        value={form.message}
                        onChange={updateField("message")}
                        placeholder={t.contact.messagePlaceholder}
                        data-testid="contact-message"
                        className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#0022FF] focus:ring-2 focus:ring-[#0022FF]/20 transition resize-none"
                      />
                    </div>

                    {status.type === "error" && (
                      <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm" data-testid="contact-form-error">
                        {status.message}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status.type === "loading"}
                      data-testid="contact-form-submit"
                      className="inline-flex items-center justify-center gap-2 rounded-full btn-shk-primary w-full sm:w-auto px-8 py-4 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {status.type === "loading" ? t.contact.submitting : t.contact.submit}
                      <Send className="h-4 w-4" />
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, type = "text", required = false, testId, value, onChange }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#0022FF] focus:ring-2 focus:ring-[#0022FF]/20 transition"
    />
  </div>
);
